import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, ExternalLink, Newspaper, Share2 } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import SEO from '../../components/seo/SEO';
import { getNewsItem } from '../../api/client';
import type { ApiResponse } from '../../types/api';

interface NewsItem {
  id: number;
  title: string;
  description: string;
  summary: string;
  content: string;
  source_name: string;
  source_url: string;
  image_url: string;
  category: string;
  news_date: string;
  published_at: string;
}

export default function NewsDetailScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getNewsItem(Number(id))
      .then((res: ApiResponse) => {
        if (res.news) {
          setItem(res.news);
        } else {
          setError('News not found');
        }
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || 'Failed to load news');
        setLoading(false);
      });
  }, [id]);

  const handleShare = async () => {
    if (navigator.share && item) {
      try {
        await navigator.share({
          title: item.title,
          text: item.summary || item.description,
          url: window.location.href,
        });
      } catch {
        // user cancelled
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  const categoryColors: Record<string, string> = {
    'AI': 'bg-blue-50 text-blue-600',
    'AI Finance': 'bg-emerald-50 text-emerald-600',
    'AI Investments': 'bg-violet-50 text-violet-600',
    'AI Marketing': 'bg-amber-50 text-amber-600',
    'AI Layoffs': 'bg-rose-50 text-rose-600',
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-3xl px-4">
          <div className="h-64 bg-slate-200 rounded-2xl" />
          <div className="h-8 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-200 rounded w-1/2" />
          <div className="h-32 bg-slate-200 rounded" />
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Article not found</h2>
          <p className="text-slate-500 mb-4">The article you're looking for doesn't exist or has been removed.</p>
          <Link to="/news" className="text-amber-500 font-semibold hover:underline">
            Back to News
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={item.title}
        description={item.summary || item.description}
        image={item.image_url}
        type="article"
        canonicalPath={`/news/${item.id}`}
      />

      {/* Hero Image */}
      <div className="h-56 sm:h-72 lg:h-80 overflow-hidden bg-slate-100">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={item.title}
            className="w-full h-full object-cover"
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Newspaper className="w-16 h-16 text-slate-300" />
          </div>
        )}
      </div>

      <article className="max-w-3xl mx-auto px-4 py-10">
        {/* Back link */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Link
            to="/news"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-amber-500 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back to AI Daily News
          </Link>
        </motion.div>

        {/* Category & Meta */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center gap-3 mb-4"
        >
          <span className={`px-3 py-1 rounded-full text-xs font-bold ${categoryColors[item.category] || 'bg-slate-100 text-slate-600'}`}>
            {item.category}
          </span>
          <span className="text-sm text-slate-500 font-medium">{item.source_name}</span>
          <span className="text-slate-300">•</span>
          <span className="flex items-center gap-1 text-sm text-slate-500">
            <Calendar className="w-3.5 h-3.5" />
            {item.news_date
              ? new Date(item.news_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
              : ''}
          </span>
          {item.published_at && (
            <span className="flex items-center gap-1 text-sm text-slate-500">
              <Clock className="w-3.5 h-3.5" />
              {new Date(item.published_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 leading-tight"
        >
          {item.title}
        </motion.h1>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="flex items-center gap-3 mb-8 pb-6 border-b border-slate-100"
        >
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 transition-colors"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
          >
            Read Full Article <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>

        {/* AI Summary Box */}
        {item.summary && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8 p-5 bg-amber-50 rounded-xl border border-amber-100"
          >
            <h3 className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-2">Quick Summary</h3>
            <p className="text-slate-800 leading-relaxed text-sm">{item.summary}</p>
          </motion.div>
        )}

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="text-base text-slate-700 leading-relaxed mb-8"
        >
          {item.description}
        </motion.p>

        {/* Full Content (if available and not placeholder) */}
        {item.content && item.content !== 'ONLY AVAILABLE IN PAID PLANS' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-headings:font-bold prose-p:text-slate-600 prose-p:leading-relaxed prose-a:text-amber-600 prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: item.content }}
          />
        )}

        {/* Source CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mt-10 p-6 bg-slate-50 rounded-2xl border border-slate-100"
        >
          <h3 className="text-lg font-bold text-slate-900 mb-2">Continue Reading</h3>
          <p className="text-sm text-slate-600 mb-4">
            This article is sourced from {item.source_name}. Visit the original publisher for the complete story.
          </p>
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors"
          >
            Read on {item.source_name} <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </article>

      <Footer />
    </div>
  );
}
